/**
 * Lift2Fit — app-level presentation primitives.
 *
 * The landing page's primitives (components/ui.tsx) are reused wherever they
 * fit; this file only adds the post-login vocabulary: metric tiles that take
 * their label from the training track, the week strip, the inline-SVG trend,
 * roster bars and honest empty/loading states.
 *
 * DESIGN BAR: white space, hairline borders, ink for structure, ONE orange
 * action per view, green/red only as small status highlights (colour + glyph +
 * word — never colour alone).
 */
import type { AnchorHTMLAttributes, ComponentType, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import { BarbellGlyph } from '@/components/Logo';
import { Card } from '@/components/ui';
import { cn, formatCompactCount } from '@/lib/format';
import {
  AlertIcon,
  ArrowRightIcon,
  BarbellIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  DumbbellIcon,
  GridIcon,
  LockIcon,
  ShieldIcon,
  SparkIcon,
  SyncIcon,
  TargetIcon,
  TimerIcon,
  TrendDownIcon,
  TrendUpIcon,
  type IconProps,
} from '@/lib/icons';
import { kgToDisplay } from '@/lib/units';
import type { AlertEntry, Highlight, TrackMetrics, TrainingTrack, TrendPoint, Week } from '@/lib/api';
import { trackMeta, type TrackIconKey } from './demoData';
import { Link } from './router';

/* ---------------------------------------------------------------------------
   Track identity
   ------------------------------------------------------------------------ */

export const TRACK_ICONS: Record<TrackIconKey, ComponentType<IconProps>> = {
  strength: BarbellIcon,
  hypertrophy: DumbbellIcon,
  conditioning: TimerIcon,
  performance: TargetIcon,
  wellness: ShieldIcon,
  general: GridIcon,
};

export function TrackIcon({ track, className }: { track: TrainingTrack; className?: string }) {
  const Icon = TRACK_ICONS[trackMeta(track).icon];
  return <Icon className={className} />;
}

/** "Conditioning Coach" — the specialist identity, never a generic coach. */
export function PersonaBadge({ track, persona, className }: { track: TrainingTrack; persona?: string | null; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-pill border border-hairline bg-page-subtle px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink',
        className,
      )}
    >
      <TrackIcon track={track} className="text-[14px] text-accent" />
      {persona ?? trackMeta(track).persona}
    </span>
  );
}

export function TrackBadge({ track, className }: { track: TrainingTrack; className?: string }) {
  const meta = trackMeta(track);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-pill border border-hairline px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted',
        className,
      )}
    >
      <TrackIcon track={track} className="text-[14px]" />
      {meta.label}
    </span>
  );
}

/* ---------------------------------------------------------------------------
   Structure
   ------------------------------------------------------------------------ */

export function ScreenHeader({
  eyebrow,
  title,
  lead,
  actions,
  badge,
}: {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="flex min-w-0 flex-col gap-2">
        {eyebrow ? (
          <p className="text-label font-bold uppercase tracking-[0.08em] text-accent">{eyebrow}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-h2 font-black uppercase leading-none tracking-[-0.01em] text-ink md:text-h1">
            {title}
          </h1>
          {badge}
        </div>
        {lead ? <p className="max-w-[68ch] text-[15px] text-ink-muted">{lead}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
  as = 'section',
  shape = 'card',
  testId,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  as?: 'section' | 'div' | 'article';
  /** `chat` opts into the chat's softer shape language. */
  shape?: 'card' | 'chat';
  /** Stable hook for the browser suite; the panel is otherwise unlabelled. */
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="contents">
      <Card shape={shape} as={as} className={cn('flex flex-col', className)}>
        {title ? (
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-hairline px-5 py-4">
            <div className="flex min-w-0 flex-col gap-1">
              <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">{title}</h2>
              {subtitle ? <p className="text-[13px] text-ink-muted">{subtitle}</p> : null}
            </div>
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
          </div>
        ) : null}
        <div className={cn('flex flex-col gap-4 p-5', bodyClassName)}>{children}</div>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   In-app button-styled link.

   The same visual vocabulary as components/ui.tsx's `LinkButton`, but routed
   through the app router so a "Start session" / "Open The Grid" action stays
   inside the signed-in shell instead of navigating to the landing page.
   ------------------------------------------------------------------------ */

type AppLinkVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
type AppLinkSize = 'sm' | 'md' | 'lg';

const APP_LINK_VARIANTS: Record<AppLinkVariant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hover active:translate-y-px',
  secondary: 'bg-brand-charcoal text-brand-white hover:opacity-90 active:translate-y-px',
  ghost: 'bg-transparent text-ink hover:bg-page-subtle',
  outline: 'bg-transparent text-ink border border-hairline hover:border-accent hover:text-accent',
};

const APP_LINK_SIZES: Record<AppLinkSize, string> = {
  sm: 'h-9 px-3 text-[12px] tracking-[0.08em]',
  md: 'h-11 px-5 text-[13px] tracking-[0.08em]',
  lg: 'h-14 px-7 text-[14px] tracking-[0.08em]',
};

export function AppLinkButton({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: { to: string; variant?: AppLinkVariant; size?: AppLinkSize; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-none font-bold uppercase select-none transition-all duration-150',
        APP_LINK_VARIANTS[variant],
        APP_LINK_SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </Link>
  );
}

/* ---------------------------------------------------------------------------
   Numbers
   ------------------------------------------------------------------------ */
export type TileTone = 'default' | 'accent' | 'success' | 'warning' | 'danger';

const TILE_TONES: Record<TileTone, string> = {
  default: 'text-ink',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

export function MetricTile({
  label,
  value,
  hint,
  hintTone = 'default',
  tone = 'default',
  icon,
  className,
  valueTitle,
  valueTestId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** status colour is reserved for the small hint line, never the big number */
  hintTone?: TileTone;
  tone?: TileTone;
  icon?: ReactNode;
  className?: string;
  /** The exact figure, when `value` is a compacted summary rendering. */
  valueTitle?: string;
  valueTestId?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5 border border-hairline bg-surface p-4', className)}>
      <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
        {icon ? <span className="text-[14px] text-ink-muted">{icon}</span> : null}
        {label}
      </span>
      {/* `min-w-0` + `break-words`: a figure that still cannot fit wraps inside
          the border instead of painting over it. Never clipped with overflow. */}
      <span
        data-testid={valueTestId}
        title={valueTitle}
        className={cn('l2f-tabular min-w-0 break-words font-mono text-[26px] font-bold leading-none', TILE_TONES[tone])}
      >
        {value}
      </span>
      {hint ? (
        <span className={cn('text-[12px]', hintTone === 'default' ? 'text-ink-muted' : TILE_TONES[hintTone])}>{hint}</span>
      ) : null}
    </div>
  );
}

export function StatRow({
  label,
  value,
  tone = 'default',
  valueTitle,
}: {
  label: string;
  value: ReactNode;
  tone?: TileTone;
  valueTitle?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-hairline py-2 last:border-b-0">
      <span className="text-[12px] uppercase tracking-[0.08em] text-ink-muted">{label}</span>
      <span
        title={valueTitle}
        className={cn('l2f-tabular min-w-0 break-words text-right font-mono text-[15px] font-bold', TILE_TONES[tone])}
      >
        {value}
      </span>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Status — always colour + glyph + word
   ------------------------------------------------------------------------ */

export type ChipTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'border-hairline text-ink-muted',
  accent: 'border-accent text-accent',
  success: 'border-success text-success',
  warning: 'border-warning text-warning',
  danger: 'border-danger text-danger',
  info: 'border-info text-info',
};

export function StatusChip({
  tone = 'neutral',
  icon,
  children,
  className,
}: {
  tone?: ChipTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      data-testid="status-chip"
      data-tone={tone}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]',
        CHIP_TONES[tone],
        className,
      )}
    >
      {icon ? <span className="text-[13px]">{icon}</span> : null}
      {children}
    </span>
  );
}

const ALERT_TONES: Record<AlertEntry['severity'], ChipTone> = {
  info: 'info',
  warning: 'warning',
  danger: 'danger',
};

const ALERT_WORDS: Record<AlertEntry['severity'], string> = {
  info: 'Note',
  warning: 'Watch',
  danger: 'Action',
};

export function AlertChips({ alerts }: { alerts: AlertEntry[] }) {
  if (!alerts.length) return null;
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Coach alerts">
      {alerts.map((alert) => (
        <li key={alert.code}>
          <StatusChip tone={ALERT_TONES[alert.severity]} icon={<AlertIcon />}>
            {ALERT_WORDS[alert.severity]}
          </StatusChip>
          <span className="ml-2 text-[13px] text-ink">{alert.message}</span>
        </li>
      ))}
    </ul>
  );
}

const FLAG_META: Record<string, { label: string; tone: ChipTone; icon: ComponentType<IconProps> }> = {
  missed_2_weeks: { label: 'Missed 2 weeks', tone: 'danger', icon: AlertIcon },
  pain_reported: { label: 'Pain reported', tone: 'danger', icon: AlertIcon },
  rpe_creep: { label: 'RPE creep', tone: 'warning', icon: TrendUpIcon },
  nothing_scheduled: { label: 'Nothing scheduled', tone: 'warning', icon: TimerIcon },
  invite_pending: { label: 'Invite pending', tone: 'info', icon: SyncIcon },
  low_adherence: { label: 'Low adherence', tone: 'warning', icon: TrendDownIcon },
};

export function flagMeta(code: string) {
  return (
    FLAG_META[code] ?? {
      label: code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      tone: 'neutral' as ChipTone,
      icon: AlertIcon,
    }
  );
}

export function FlagChips({ flags }: { flags: string[] }) {
  if (!flags.length) {
    return (
      <StatusChip tone="success" icon={<CheckIcon />}>
        On track
      </StatusChip>
    );
  }
  return (
    <span className="flex flex-wrap gap-1.5">
      {flags.map((flag) => {
        const meta = flagMeta(flag);
        const Icon = meta.icon;
        return (
          <StatusChip key={flag} tone={meta.tone} icon={<Icon />}>
            {meta.label}
          </StatusChip>
        );
      })}
    </span>
  );
}

/**
 * Adherence = a thin bar, never a table cell.
 *
 * The bar and its number are clamped to the SAME 0–100 range, because a bar that
 * reads "full" beside a label reading "102.5%" is worse than either alone — the
 * two disagreed and the coach noticed. The server caps adherence at 100 at the
 * source (migration 0023); this clamp is the belt-and-braces so the label can
 * never contradict the bar even if a value arrives out of range.
 */
export function AdherenceBar({ pct, className }: { pct: number | null; className?: string }) {
  if (pct === null) {
    return <span className={cn('text-[12px] text-ink-muted', className)}>No data shared yet</span>;
  }
  const bounded = Math.max(0, Math.min(100, pct));
  const tone = bounded >= 80 ? 'bg-success' : bounded >= 60 ? 'bg-warning' : 'bg-danger';
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-pill bg-page-subtle" role="presentation">
        <span className={cn('block h-full rounded-pill', tone)} style={{ width: `${bounded}%` }} />
      </span>
      <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">{bounded}%</span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
   The week strip
   ------------------------------------------------------------------------ */

const DAY_STATES: Record<Week['days'][number]['state'], { word: string; tone: ChipTone; icon?: ReactNode }> = {
  done: { word: 'Done', tone: 'success', icon: <CheckIcon /> },
  today: { word: 'Today', tone: 'accent' },
  rest: { word: 'Rest', tone: 'neutral' },
  missed: { word: 'Missed', tone: 'danger', icon: <CloseIcon /> },
  upcoming: { word: '—', tone: 'neutral' },
};

export function WeekStrip({ week, className }: { week: Week; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <ol className="grid grid-cols-7 gap-1.5" aria-label={`Week of ${week.weekStart}`}>
        {week.days.map((day) => {
          const meta = DAY_STATES[day.state];
          const weekday = new Date(`${day.date}T00:00:00Z`).toLocaleDateString('en-GB', {
            weekday: 'short',
            timeZone: 'UTC',
          });
          return (
            <li
              key={day.date}
              className={cn(
                'flex min-h-[64px] flex-col items-center justify-center gap-1 border bg-surface px-1 py-2 text-center',
                day.state === 'today' ? 'border-accent' : 'border-hairline',
                day.state === 'missed' && 'border-danger',
                day.state === 'done' && 'border-success',
              )}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">{weekday}</span>
              <span
                className={cn(
                  'text-[13px] leading-none',
                  meta.tone === 'success' && 'text-success',
                  meta.tone === 'accent' && 'text-accent',
                  meta.tone === 'danger' && 'text-danger',
                  meta.tone === 'neutral' && 'text-ink-muted',
                )}
              >
                {meta.icon}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-ink-muted">{meta.word}</span>
            </li>
          );
        })}
      </ol>
      <p className="text-[13px] text-ink-muted">
        <span className="l2f-tabular font-mono font-bold text-ink">{week.completed}</span> of{' '}
        <span className="l2f-tabular font-mono font-bold text-ink">{week.target}</span> sessions logged this week
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The 8-week trend — inline SVG bars, no chart library
   ------------------------------------------------------------------------ */

export type TrendSeries = 'volumeKg' | 'intervalMinutes' | 'mobilitySessions' | 'sessions' | 'sets';

export function pickTrendSeries(points: TrendPoint[]): TrendSeries {
  const has = (key: TrendSeries) => points.some((point) => typeof point[key] === 'number' && point[key] !== null);
  if (has('volumeKg')) return 'volumeKg';
  if (has('intervalMinutes')) return 'intervalMinutes';
  if (has('mobilitySessions')) return 'mobilitySessions';
  if (has('sessions')) return 'sessions';
  return 'sets';
}

export function trendSeriesLabel(series: TrendSeries, unit: UnitSystem): string {
  if (series === 'volumeKg') return unit === 'imperial' ? 'Volume (lb)' : 'Volume (kg)';
  if (series === 'intervalMinutes') return 'Interval minutes';
  if (series === 'mobilitySessions') return 'Mobility sessions';
  if (series === 'sessions') return 'Sessions';
  return 'Sets';
}

export function TrendBars({
  points,
  unit,
  series,
  className,
}: {
  points: TrendPoint[];
  unit: UnitSystem;
  series?: TrendSeries;
  className?: string;
}) {
  const active = series ?? pickTrendSeries(points);
  const label = trendSeriesLabel(active, unit);
  const values = points.map((point) => {
    const raw = Number(point[active] ?? 0);
    return active === 'volumeKg' && unit === 'imperial' ? kgToDisplay(raw, unit) : Math.round(raw * 10) / 10;
  });
  const max = Math.max(1, ...values);
  const latest = values[values.length - 1] ?? 0;
  const width = 320;
  const height = 132;
  const chartTop = 10;
  const chartBottom = 104;
  const slot = width / Math.max(1, values.length);
  const barWidth = Math.min(28, slot * 0.62);
  const description = values.map((value, index) => `week ${index + 1}: ${value}`).join(', ');

  return (
    <figure className={cn('flex flex-col gap-2', className)}>
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label} · 8 weeks</span>
        <span className="l2f-tabular font-mono text-[15px] font-bold text-ink">
          {latest.toLocaleString('en-GB')}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-[132px] w-full"
        role="img"
        aria-label={`${label} over the last 8 weeks. ${description}`}
      >
        <line x1="0" y1={chartBottom} x2={width} y2={chartBottom} stroke="var(--l2f-border)" strokeWidth="1" />
        {values.map((value, index) => {
          const barHeight = Math.max(2, (value / max) * (chartBottom - chartTop));
          const x = index * slot + (slot - barWidth) / 2;
          const y = chartBottom - barHeight;
          const isLatest = index === values.length - 1;
          return (
            <g key={points[index]?.weekStart ?? index}>
              <title>{`Week ${index + 1}: ${value}`}</title>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                className={isLatest ? 'l2f-bar-accent' : 'l2f-bar'}
              />
              <text
                x={index * slot + slot / 2}
                y={height - 12}
                textAnchor="middle"
                fontSize="9"
                fill="var(--l2f-text-muted)"
                fontFamily="var(--l2f-font-mono)"
              >
                {index + 1}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-[12px] text-ink-muted">Week 1 – 8, oldest to newest. Latest week in orange.</p>
    </figure>
  );
}

/* ---------------------------------------------------------------------------
   Loading and empty states
   ------------------------------------------------------------------------ */

export function Skeleton({ className }: { className?: string }) {
  return <span className={cn('l2f-skeleton block h-4 w-full', className)} aria-hidden="true" />;
}

/** Skeleton panel — used while a dashboard or roster is in flight. */
export function LoadingPanel({ label, rows = 3, className }: { label: string; rows?: number; className?: string }) {
  return (
    <div
      className={cn('flex flex-col gap-3 border border-hairline bg-surface p-5', className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={index === 0 ? 'h-6 w-2/3' : 'h-4 w-full'} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
  className,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-start gap-3 border border-dashed border-hairline bg-surface p-6', className)}>
      <span className="text-ink-muted">
        {icon ?? <BarbellGlyph className="h-8 w-8 text-accent" />}
      </span>
      <h3 className="text-[15px] font-bold uppercase tracking-[0.04em] text-ink">{title}</h3>
      <p className="max-w-[60ch] text-[14px] text-ink-muted">{body}</p>
      {action ? <div className="flex flex-wrap gap-2 pt-1">{action}</div> : null}
    </div>
  );
}

/** Announced async outcomes (errors, confirmations) without stealing focus. */
export function LiveMessage({ message, tone = 'info' }: { message: string | null; tone?: ChipTone }) {
  return (
    <p
      aria-live="polite"
      className={cn(
        'min-h-[20px] text-[13px]',
        tone === 'danger' && 'text-danger',
        tone === 'success' && 'text-success',
        tone === 'warning' && 'text-warning',
        tone === 'info' && 'text-ink-muted',
        tone === 'neutral' && 'text-ink-muted',
        tone === 'accent' && 'text-accent',
      )}
    >
      {message ?? ''}
    </p>
  );
}

/* ---------------------------------------------------------------------------
   Highlights (a track-appropriate replacement for the PR list)
   ------------------------------------------------------------------------ */

export function HighlightList({ highlights, className }: { highlights: Highlight[]; className?: string }) {
  return (
    <ul className={cn('flex flex-col divide-y divide-hairline', className)}>
      {highlights.map((item) => (
        <li key={`${item.label}-${item.at}`} className="flex items-baseline justify-between gap-4 py-2.5">
          <span className="flex min-w-0 flex-col">
            <span className="text-[14px] font-bold text-ink">{item.label}</span>
            {item.detail ? <span className="text-[12px] text-ink-muted">{item.detail}</span> : null}
          </span>
          <span className="l2f-tabular shrink-0 font-mono text-[14px] font-bold text-ink">{item.value}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------------------
   Metric focus — labels come from the track, never hard-coded
   ------------------------------------------------------------------------ */

export interface HeadlineMetric {
  label: string;
  value: string;
  hint: string;
  tone: TileTone;
  hintTone?: TileTone;
  /** The exact figure when `value` is a compacted summary rendering. */
  title?: string;
}

const num = (value: number | null | undefined, format: (n: number) => string, fallback = '—'): string =>
  value === null || value === undefined ? fallback : format(value);

export function formatSigned(value: number | null | undefined, suffix = '%'): string {
  if (value === null || value === undefined) return '—';
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}${suffix}`;
}

/**
 * The four headline numbers for a track. Strength leads with tonnage, wellness
 * with how the client feels — the labels always come from `metricsFocus`.
 *
 * Both `metrics` and `totals` are optional on purpose: the live API may omit
 * `trackMetrics` entirely (its track aggregates ride inside `totals`), and a
 * missing field must render as an em dash rather than throw.
 */
export const EMPTY_TRACK_METRICS: TrackMetrics = {
  coachPersona: 'Performance Coach',
  bestE1rmKg: null,
  topSetLabel: null,
  volumeKg7: null,
  volumeChangePct: null,
  sets7: null,
  prs7: null,
  setsPerMuscle: null,
  bodyweightKg: null,
  intervalMinutes: null,
  workRestRatio: null,
  ergMetres: null,
  avgRpe: null,
  powerMarkers: null,
  jumpHeightCm: null,
  readinessPct: null,
  mobilitySessions: null,
  painScore: null,
  consistencyPct: null,
  sessions7: 0,
  feel: null,
};

export interface HeadlineTotals {
  volumeKg7?: number;
  volumeChangePct?: number | null;
  sets7?: number;
  prs7?: number;
  avgRpe7?: number | null;
  compliancePct7?: number | null;
}

export function headlineMetrics(
  track: TrainingTrack,
  metricsInput: TrackMetrics | null | undefined,
  unit: UnitSystem,
  totalsInput?: HeadlineTotals | null,
): HeadlineMetric[] {
  const metrics = metricsInput ?? EMPTY_TRACK_METRICS;
  const totals = {
    volumeKg7: totalsInput?.volumeKg7 ?? 0,
    volumeChangePct: totalsInput?.volumeChangePct ?? null,
    sets7: totalsInput?.sets7 ?? 0,
    prs7: totalsInput?.prs7 ?? 0,
    avgRpe7: totalsInput?.avgRpe7 ?? null,
    compliancePct7: totalsInput?.compliancePct7 ?? null,
  };
  const focus = trackMeta(track).metricsFocus;
  // A headline tile: compact once the weekly total outgrows a small box, with
  // the exact figure still on the block/session detail the tile links to.
  const volumeValue = unit === 'imperial' ? kgToDisplay(totals.volumeKg7, unit) : totals.volumeKg7;
  const volume = `${formatCompactCount(volumeValue)} ${unit === 'imperial' ? 'lb' : 'kg'}`;
  const volumeTitle = `${Math.round(volumeValue).toLocaleString('en-GB')} ${unit === 'imperial' ? 'lb' : 'kg'}`;
  const volumeMetric = { value: volume, ...(volume === volumeTitle ? {} : { title: volumeTitle }) };
  const ergMetres = metrics.ergMetres;
  const ergValue = num(ergMetres, (n) => `${formatCompactCount(n)} m`);
  const ergTitle = num(ergMetres, (n) => `${Math.round(n).toLocaleString('en-GB')} m`, '');
  const ergMetric = { value: ergValue, ...(ergValue === ergTitle || ergTitle === '' ? {} : { title: ergTitle }) };
  const change = totals.volumeChangePct === null ? 'vs last week: —' : `${formatSigned(totals.volumeChangePct)} vs last week`;
  const changeTone: TileTone =
    totals.volumeChangePct === null ? 'default' : totals.volumeChangePct >= 0 ? 'success' : 'danger';
  const compliance = totals.compliancePct7;
  const consistency = metrics.consistencyPct ?? compliance;

  switch (track) {
    case 'strength':
      return [
        { label: focus[0], ...volumeMetric, hint: change, tone: 'default', hintTone: changeTone },
        { label: focus[1], value: num(metrics.bestE1rmKg, (n) => `${Math.round(n * 10) / 10} kg`), hint: 'estimated 1RM, best lift', tone: 'default' },
        { label: focus[2], value: metrics.topSetLabel ?? '—', hint: 'heaviest clean set this week', tone: 'default' },
        {
          label: 'Compliance',
          value: num(compliance, (n) => `${n}%`),
          hint: `${metrics.sessions7} sessions logged`,
          tone: 'default',
          hintTone: compliance !== null && compliance < 80 ? 'warning' : 'success',
        },
      ];
    case 'hypertrophy':
      return [
        { label: focus[0], ...volumeMetric, hint: change, tone: 'default', hintTone: changeTone },
        { label: focus[1], value: num(metrics.setsPerMuscle, (n) => `${n}`), hint: 'working sets per muscle, weekly', tone: 'default' },
        { label: focus[2], value: num(metrics.bodyweightKg, (n) => `${Math.round(n * 10) / 10} kg`), hint: 'self-reported, weekly', tone: 'default' },
        {
          label: 'Compliance',
          value: num(compliance, (n) => `${n}%`),
          hint: `${totals.sets7} sets logged`,
          tone: 'default',
          hintTone: compliance !== null && compliance < 80 ? 'warning' : 'success',
        },
      ];
    case 'conditioning':
      return [
        { label: focus[0], value: num(metrics.intervalMinutes, (n) => `${n} min`), hint: 'hard minutes this week', tone: 'default' },
        { label: focus[1], value: metrics.workRestRatio ?? '—', hint: 'target ratio across interval blocks', tone: 'default' },
        { label: focus[2], ...ergMetric, hint: 'rower · ski erg · bike erg', tone: 'default' },
        { label: 'Avg RPE', value: num(metrics.avgRpe, (n) => `${n}`), hint: `${metrics.sessions7} interval sessions`, tone: 'default' },
      ];
    case 'performance':
      return [
        { label: focus[0], value: num(metrics.powerMarkers, (n) => `${n}`), hint: 'jumps & throws recorded', tone: 'default' },
        { label: focus[1], value: num(metrics.jumpHeightCm, (n) => `${n} cm`), hint: 'best box jump this block', tone: 'default' },
        {
          label: focus[2],
          value: num(metrics.readinessPct, (n) => `${n}%`),
          hint: 'before the last session',
          tone: metrics.readinessPct !== null && metrics.readinessPct < 80 ? 'warning' : 'default',
        },
        {
          label: 'Compliance',
          value: num(compliance, (n) => `${n}%`),
          hint: `${metrics.sessions7} power sessions`,
          tone: 'default',
          hintTone: compliance !== null && compliance < 80 ? 'warning' : 'success',
        },
      ];
    case 'wellness':
      return [
        { label: focus[0], value: num(metrics.mobilitySessions, (n) => `${n}`), hint: 'mobility & stability work', tone: 'default' },
        {
          label: focus[1],
          value: num(metrics.painScore, (n) => `${Math.round(n * 10) / 10} / 10`),
          hint: metrics.feel ?? 'self-reported, daily',
          tone: metrics.painScore !== null && metrics.painScore >= 4 ? 'danger' : 'default',
        },
        {
          label: focus[2],
          value: num(consistency, (n) => `${n}%`),
          hint: 'sessions completed as planned',
          tone: 'default',
          hintTone: consistency !== null && consistency < 80 ? 'warning' : 'success',
        },
        {
          label: 'How you feel',
          value: metrics.feel ? metrics.feel.split(',')[0] ?? metrics.feel : '—',
          hint: metrics.feel ?? 'no check-in yet',
          tone: 'default',
        },
      ];
    default:
      return [
        { label: focus[0], ...volumeMetric, hint: change, tone: 'default', hintTone: changeTone },
        { label: focus[1], value: String(metrics.sessions7), hint: 'sessions in the last 7 days', tone: 'default' },
        {
          label: focus[2],
          value: num(consistency, (n) => `${n}%`),
          hint: 'weeks you trained as planned',
          tone: 'default',
          hintTone: consistency !== null && consistency < 80 ? 'warning' : 'success',
        },
        {
          label: 'PRs',
          value: String(totals.prs7),
          hint: 'personal records this week',
          tone: 'default',
          hintTone: totals.prs7 > 0 ? 'success' : 'default',
        },
      ];
  }
}

export { ArrowRightIcon };

/* ---------------------------------------------------------------------------
   Field help — the ⓘ popover and the labelled field that carries it.

   Every control in an add/edit form owns a short, plain-English explanation of
   what it does and how the app uses it. The popover is a real button with an
   accessible name, `aria-expanded` and `aria-describedby`; Escape and a
   click/tap outside close it and Escape returns focus to the trigger. It never
   moves or traps focus, so it is safe mid-form on a phone.
   ------------------------------------------------------------------------ */

export function InfoPopover({
  label,
  testId,
  children,
  className,
}: {
  /** the field name, used for the trigger's accessible name */
  label: string;
  /** stable hook for the browser suite; defaults to a slug of the label */
  testId?: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = `field-help-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const slug = testId ?? label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = 248;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    // Flip above when there is no room below — the form stays readable on a phone.
    const top = rect.bottom + 100 > window.innerHeight && rect.top > 110 ? rect.top - 96 : rect.bottom + 6;
    setCoords({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <span ref={wrapperRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`info-${slug}`}
        aria-label={`More about ${label}`}
        aria-expanded={open}
        aria-describedby={open ? panelId : undefined}
        onClick={(event) => {
          // Inside a <label> the default action would focus the control; keep the
          // help button self-contained.
          event.preventDefault();
          event.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          place();
          setOpen(true);
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-hairline text-[10px] font-bold leading-none text-ink-muted transition-colors duration-150 hover:border-accent hover:text-accent focus-visible:border-accent focus-visible:text-accent"
      >
        <span aria-hidden="true">i</span>
      </button>
      {open ? (
        <span
          role="tooltip"
          id={panelId}
          data-testid={`info-panel-${slug}`}
          style={coords ? { position: 'fixed', top: coords.top, left: coords.left } : undefined}
          className="z-[80] block w-[248px] border border-hairline bg-brand-charcoal px-3 py-2 text-[12px] font-normal normal-case leading-snug tracking-normal text-brand-white shadow-modal"
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}

/** A field label plus its ⓘ help. The input itself keeps an `aria-label`. */
export function Field({
  label,
  info,
  testId,
  className,
  children,
}: {
  label: string;
  info?: ReactNode;
  testId?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
        {info ? (
          <InfoPopover label={label} testId={testId}>
            {info}
          </InfoPopover>
        ) : null}
      </span>
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Grouping — one vocabulary for supersets and drop sets.
   ------------------------------------------------------------------------ */

const GROUP_LETTERS = 'ABCDEFGHIJKL';

/** 1 → A, 2 → B … (the landing page's group badge). */
export function groupLetter(group: number | null): string {
  if (group === null || group < 1) return '';
  return GROUP_LETTERS[group - 1] ?? String(group);
}

/** The tag text used on both screens: `SUPERSET A` / `DROP SET A`. */
export function groupTagText(group: number | null, type: 'superset' | 'drop_set' | null): string {
  if (group === null) return '';
  return `${type === 'drop_set' ? 'Drop set' : 'Superset'} ${groupLetter(group)}`;
}

/* ---------------------------------------------------------------------------
   Notes — collapsed by default, so the logging row stays dense.
   ------------------------------------------------------------------------ */

export function CollapsedNote({
  id,
  note,
  label = 'Notes',
  className,
}: {
  id: string;
  note: string | null;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!note) return null;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <button
        type="button"
        data-testid={`notes-toggle-${id}`}
        aria-expanded={open}
        aria-controls={`note-body-${id}`}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex w-fit items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted transition-colors duration-150 hover:text-accent"
      >
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent/70" />
        {label}
        <ChevronDownIcon className={cn('text-[11px] transition-transform duration-150', open && 'rotate-180')} />
      </button>
      {open ? (
        <p id={`note-body-${id}`} data-testid={`notes-body-${id}`} className="max-w-[70ch] text-[12px] leading-snug text-ink-muted">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The locked AI-coach slot.

   AI coach output is PREMIUM and is never editable, and the app never invents a
   coach line for a plan the AI did not write. Where the coach's next-block
   projection would appear, a locked slot says exactly that and offers the one
   way to unlock it. It holds no input, so no EDIT control can reach it.
   ------------------------------------------------------------------------ */

export function LockedCoachSlot({
  planSource,
  testId = 'coach-locked',
  className,
}: {
  planSource: string | null;
  testId?: string;
  className?: string;
}) {
  const manual = planSource === 'manual';
  return (
    <section
      data-testid={testId}
      aria-label="AI coach — next block projection (locked)"
      className={cn(
        'flex flex-col gap-3 border border-hairline border-l-2 border-l-brand-charcoal bg-page-subtle px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <LockIcon className="mt-0.5 shrink-0 text-[14px] text-ink-muted" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            <span className="flex items-center gap-1.5 text-ink">
              <SparkIcon className="text-[12px] text-accent" /> AI coach · next block projection
            </span>
            <StatusChip tone="neutral">Locked</StatusChip>
          </span>
          <p className="max-w-[78ch] text-[12px] leading-snug text-ink-muted">
            {manual
              ? 'No AI projection is written for a plan you built yourself — this slot is where the coach would put one. Your numbers here are your own; nothing on this row changes it.'
              : 'The coach\u2019s next-block projection is a Premium feature and is not written into your log. Unlock the AI coach to have it read your logged sets and project the next block.'}
          </p>
        </div>
      </div>
      <Link
        to="/plan/new"
        data-testid={`${testId}-unlock`}
        className="inline-flex h-10 shrink-0 items-center gap-2 border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink transition-colors duration-150 hover:border-accent hover:text-accent"
      >
        <LockIcon className="text-[12px]" /> Unlock AI coach
      </Link>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Previous session — what this movement did last time, if anything.
   ------------------------------------------------------------------------ */

export function PreviousSetLine({ sets, className }: { sets: string[]; className?: string }) {
  if (!sets.length) {
    return <span className={cn('text-[11px] text-ink-muted', className)}>First time logged</span>;
  }
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-ink-muted">Last</span>
      {sets.map((entry, index) => (
        <span
          key={`${entry}-${index}`}
          className="l2f-tabular rounded-sm border border-hairline bg-page-subtle px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink-muted"
        >
          {entry}
        </span>
      ))}
    </span>
  );
}
